import { useEffect, useRef } from 'react'

export const PCLN_AD_TARGET_HEIGHT_MESSAGE_TYPE = 'PCLN_AD_TARGET_HEIGHT'

export type UsePostAdTargetHeightParams = {
  enabled: boolean
}

const ALLOWED_IFRAME_HOST_SUFFIXES = ['googlesyndication.com', 'doubleclick.net']

function getIframeTargetOrigin(iframe: HTMLIFrameElement): string | undefined {
  try {
    const origin = new URL(iframe.src, globalThis.location.href).origin
    if (!origin || origin === 'null') return undefined
    return origin
  } catch {
    return undefined
  }
}

function isAllowedIframeOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin)
    return ALLOWED_IFRAME_HOST_SUFFIXES.some(
      (suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`),
    )
  } catch {
    return false
  }
}

function postAdTargetHeight({ adEl, formEl }: { adEl: HTMLElement; formEl: HTMLElement }) {
  const height = formEl.offsetHeight
  if (height === 0) return

  const message = { type: PCLN_AD_TARGET_HEIGHT_MESSAGE_TYPE, height }

  adEl.querySelectorAll('iframe').forEach((iframe) => {
    const contentWindow = iframe.contentWindow
    if (!contentWindow) return

    const targetOrigin = getIframeTargetOrigin(iframe)
    if (!targetOrigin || !isAllowedIframeOrigin(targetOrigin)) return

    contentWindow.postMessage(message, targetOrigin)
  })
}

export function usePostAdTargetHeight({ enabled }: UsePostAdTargetHeightParams) {
  const searchFormRef = useRef<HTMLDivElement>(null)
  const adUnitRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!enabled) return

    const formEl = searchFormRef.current
    const adEl = adUnitRef.current
    if (!formEl || !adEl) return

    const boundIframes = new Set<HTMLIFrameElement>()

    const syncHeight = () => {
      postAdTargetHeight({ adEl, formEl })
    }

    const bindIframeLoadListener = (iframe: HTMLIFrameElement) => {
      if (boundIframes.has(iframe)) return

      boundIframes.add(iframe)
      iframe.addEventListener('load', syncHeight)
    }

    const bindIframeLoadListeners = () => {
      adEl.querySelectorAll('iframe').forEach((iframe) => {
        bindIframeLoadListener(iframe)
      })
    }

    const handleMutation = () => {
      bindIframeLoadListeners()
      syncHeight()
    }

    const resizeObserver = new ResizeObserver(() => {
      syncHeight()
    })
    resizeObserver.observe(formEl)

    const mutationObserver = new MutationObserver(() => {
      handleMutation()
    })
    mutationObserver.observe(adEl, { childList: true, subtree: true })

    bindIframeLoadListeners()
    syncHeight()

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      boundIframes.forEach((iframe) => {
        iframe.removeEventListener('load', syncHeight)
      })
      boundIframes.clear()
    }
  }, [enabled])

  return { searchFormRef, adUnitRef }
}
